import React, { useEffect, useState } from "react";
import { Card, Button, message, Input } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { BASE_URL } from "./config";
import "./styles/Plan.css";
import { FaAngleLeft } from "react-icons/fa"; // import left arrow icon

function PlanDetails() {
  const { type } = useParams();
  const navigate = useNavigate();

  // --------------------
  // STATES
  // --------------------
  const [plan, setPlan] = useState(null);
  const [users, setUsers] = useState(1);
  const [cycle, setCycle] = useState("annualPrepaid");

  const [coupon, setCoupon] = useState("");
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponApplied, setCouponApplied] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  const [loadingCoupon, setLoadingCoupon] = useState(false);

  // --------------------
  // FETCH PLAN DETAILS
  // --------------------
  useEffect(() => {
    console.log("Fetching plan details for:", type);
    fetch(`${BASE_URL}/plans.php?plan=${type}`)
      .then((res) => res.json())
      .then((data) => {
        console.log("Plan API response:", data);
        if (data.error) {
          message.error(data.error);
        } else {
          setPlan(data.selectedPlan);
        }
      })
      .catch((err) => {
        console.error("Error fetching plan details:", err);
        message.error("Failed to load plan details");
      });
  }, [type]);

  // --------------------
  // BASE PRICE
  // --------------------
  const pricePerUser =
    cycle === "annualPrepaid"
      ? plan?.annualPrepaid
      : cycle === "annualMonthly"
      ? plan?.annualMonthly
      : plan?.monthly;

  const totalPrice =
    cycle === "monthly"
      ? pricePerUser * users
      : pricePerUser * users;

  // --------------------
  // RESET COUPON ON CHANGE
  // --------------------
  useEffect(() => {
    setCouponApplied(false);
    setShowCoupon(false);
    setCoupon("");
    setDiscount(0);
    setFinalPrice(totalPrice);
  }, [cycle, users, totalPrice]);

  // --------------------
  // APPLY COUPON
  // --------------------
  const applyCoupon = () => {
    if (!coupon) {
      message.warning("Enter a coupon code");
      return;
    }

    setLoadingCoupon(true);
    console.log("Applying coupon:", coupon, "for plan:",type);

    fetch(`${BASE_URL}/validate-coupon.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code: coupon,
        plan: type,
        amount: totalPrice,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("Coupon API response:", data);
        if (data.error) {
          message.error(data.error);
        } else {
          // Calculate discount dynamically
          let appliedDiscount = 0;

          if (data.discount_type === "percentage") {
            appliedDiscount = Math.round(
              (totalPrice * data.discount_value) / 100
            );
          } else if (data.discount_type === "flat") {
            appliedDiscount = data.discount_value;
          }

          if (appliedDiscount > totalPrice) appliedDiscount = totalPrice;

          setDiscount(appliedDiscount);
          setFinalPrice(totalPrice - appliedDiscount);
          setCouponApplied(true);

          message.success("Coupon applied successfully!");
        }
      })
      .catch((err) => {
        console.error("Error applying coupon:", err);
        message.error("Coupon validation failed");
      })
      .finally(() => setLoadingCoupon(false));
  };

  // --------------------
  // LOADING STATE
  // --------------------
  if (!plan) {
    return (
      <h2 style={{ textAlign: "center", marginTop: 50 }}>
        Loading plan details...
      </h2>
    );
  }

  // --------------------
  // UI
  // --------------------
  return (
    <div className="page-wrapper">
    <div className="page-header" >
  {/* Back Button */}
  <button onClick={() => navigate(-1)}>
   <FaAngleLeft size={20} />
  </button>

  {/* Logo */}
 <img
          src={require("./assets/meet.png")}
          alt="Company Logo"
          className="logo"
        />
</div>


      <div className="plan-row">
        {/* LEFT SIDE */}
        <Card className="left-card">
          <h3>{plan.title}</h3>

          <h4>Select Subscription Cycle</h4>

          <div className="cycle-row">
            {[
           
              { key: "annualMonthly", label: "Annual Monthly", price: plan.annualMonthly },
              { key: "monthly", label: "Monthly", price: plan.monthly },
            ].map((c) => (
              <Card
                key={c.key}
                className={`cycle-card ${cycle === c.key ? "active-cycle" : ""}`}
                onClick={() => setCycle(c.key)}
              >
                <h5>{c.label}</h5>
                <h3>₹{c.price}/user</h3>
              </Card>
            ))}
          </div>

          <h4>Number of Users</h4>
          <Input
            type="number"
            min={1}
            value={users}
            onChange={(e) => setUsers(parseInt(e.target.value) || 1)}
          />
        </Card>

        {/* RIGHT SIDE */}
        <Card className="right-card">
          <h3>Order Summary</h3>

          <p>
            {plan.title} — {users} User(s)
          </p>

          <div className="summary-row">
            <span>Price</span>
            <span>₹{totalPrice}</span>
          </div>

          {/* COUPON SECTION */}
          <div style={{ marginBottom: 10, marginTop: 10 }}>
            {!showCoupon && !couponApplied && (
              <Button
                type="link"
                style={{ padding: 0 }}
                onClick={() => setShowCoupon(true)}
              >
                Have a coupon?
              </Button>
            )}

            {showCoupon && (
              <>
                <div style={{ display: "flex", gap: 8 }}>
                  <Input
                    placeholder="Enter coupon code"
                    value={coupon}
                    onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                    disabled={couponApplied}
                  />

                  <Button
                    type="primary"
                    onClick={applyCoupon}
                    loading={loadingCoupon}
                    disabled={couponApplied}
                  >
                    Apply
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* DISCOUNT */}
          {couponApplied && discount > 0 && (
            <div className="summary-row" style={{ marginTop: 10 }}>
              <span>Coupon Discount</span>
              <span>-₹{discount}</span>
            </div>
          )}

          <hr />

          <div className="summary-row">
            <strong>Total</strong>
            <strong>₹{finalPrice}</strong>
          </div>

          <Button
            type="primary"
            block
            style={{ marginTop: 20 }}
            onClick={() =>
              navigate("/final-booking", {
                state: {
                  amount: finalPrice,
                  plan: plan.title,
                  users,
                  cycle,
                  coupon: couponApplied ? coupon : "",
                },
              })
            }
          >
            Continue
          </Button>
        </Card>
      </div>
    </div>
  );
}

export default PlanDetails;
